/**
 * <SharedDealBanner> — informational banner shown above the BuilderTabBar
 * when the Builder is in read-only-share mode (`?share=<id>` query param).
 *
 * `external-sharing` Phase 4 Session 4.2 (2026-05-09).
 *
 * Surfaces the share's owner + title + a [Fork] CTA. The Fork action calls
 * `useForkShare()` (shipped Session 4.1) which routes to the new
 * conversation on success. Distinct visual from `<ShareForkBanner>` (which
 * marks fork-back lineage on the recipient's own conversation post-fork).
 *
 * Tokens follow `<ForkSuccessBanner>`'s convention — accent-tinted info
 * panel: `bg-accent-dim border border-accent/15 text-accent`. Lays out as
 * a single horizontal strip; icon + copy on the left, [Fork] button on the
 * right.
 */
import clsx from 'clsx';
import { Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useForkShare } from '@/hooks/use-shares';

export interface SharedDealBannerProps {
  shareId: string;
  ownerName: string;
  dealTitle: string;
  className?: string;
}

export function SharedDealBanner({
  shareId,
  ownerName,
  dealTitle,
  className,
}: SharedDealBannerProps) {
  const forkShareMutation = useForkShare();
  const isForking = forkShareMutation.isPending;

  return (
    <div
      data-testid="shared-deal-banner"
      data-share-id={shareId}
      className={clsx(
        'flex items-center justify-between gap-4 px-5 py-2.5',
        'bg-accent-dim border border-accent/15 rounded-[3px]',
        'text-accent shrink-0',
        className,
      )}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <Sparkles size={14} className="text-accent shrink-0" />
        <span className="text-sm font-body text-text-bright truncate">
          <span className="font-mono text-accent">SHARED DEAL</span>
          <span className="text-text-muted mx-2">·</span>
          <span className="font-mono text-text-bright truncate">{dealTitle}</span>
          <span className="text-text-muted"> from </span>
          <span className="font-mono text-text-bright">{ownerName}</span>
        </span>
        <span className="text-xs text-text-muted hidden md:inline">
          Read-only — fork to interact with the agent.
        </span>
      </div>
      <Button
        variant="primary"
        onClick={() => forkShareMutation.mutate(shareId)}
        disabled={isForking}
        data-testid="shared-deal-banner-fork"
        className="!py-1.5 !px-3 shrink-0"
      >
        {isForking ? 'Forking…' : 'Fork to interact'}
      </Button>
    </div>
  );
}
